"use client";

import { useLoginWithEmail } from "@privy-io/react-auth";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "logic/utils";
import { type FormEvent, type ReactNode, useState } from "react";
import { ButtonDS } from "ui/components/ButtonDS";
import z from "zod";

import { EASE, REGULAR } from "./constants";
import { TERMS_ACCEPTED_KEY, TermsCheckbox } from "./TermsCheckbox";

/**
 * One field that changes in place: the email with "Get Code", then the
 * passcode with "Resend", with the address it went to and a way back. "Login"
 * slides in beside "Resend" once a digit is typed and stays greyed out until
 * the sixth, and until the terms are agreed to.
 */
export function EmailLogin() {
  const { sendCode, loginWithCode, state: otpStatus } = useLoginWithEmail();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validEmail = z.email().safeParse(email.trim()).success;

  const requestCode = async () => {
    setError(null);
    setCode("");
    try {
      await sendCode({ email: email.trim() });
      setStep("code");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send code");
    }
  };

  const onEmailSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (validEmail) requestCode();
  };

  const onCodeSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (code.length !== 6 || !agreedToTerms) return;

    setError(null);
    await loginWithCode({ code })
      .then(() => {
        localStorage.setItem(TERMS_ACCEPTED_KEY, "true");
      })
      .catch((error: { privyErrorCode: string }) => {
        if (error.privyErrorCode === "invalid_credentials") {
          setError("Invalid passcode");
        } else {
          setError("Failed to login");
        }
      });
  };

  const changeEmail = () => {
    setError(null);
    setStep("email");
  };

  return (
    // Both steps share one height, so switching between them moves nothing
    // below (the terms and any error grow it on the code step).
    <div className="min-h-[78px]">
      <AnimatePresence initial={false} mode="wait">
        {step === "email" ? (
          <motion.form
            key="email"
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-3"
            exit={{ opacity: 0, y: -6 }}
            initial={{ opacity: 0, y: 6 }}
            onSubmit={onEmailSubmit}
            transition={{ duration: 0.25, ease: EASE }}
          >
            <Field
              action={
                <ButtonDS
                  className={REGULAR}
                  disabled={!validEmail}
                  isLoading={otpStatus.status === "sending-code"}
                  size="md"
                  type="submit"
                >
                  Get Code
                </ButtonDS>
              }
            >
              <input
                aria-label="Email address"
                autoComplete="email"
                className={INPUT_CLASS}
                id="email-input"
                name="email"
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                placeholder="Enter your email"
                type="email"
                value={email}
              />
            </Field>

            <ErrorLine error={error} />
          </motion.form>
        ) : (
          <motion.form
            key="code"
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-3"
            exit={{ opacity: 0, y: -6 }}
            initial={{ opacity: 0, y: 6 }}
            onSubmit={onCodeSubmit}
            transition={{ duration: 0.25, ease: EASE }}
          >
            <Field
              action={
                <>
                  <AnimatePresence initial={false}>
                    {code.length > 0 ? (
                      <motion.span
                        key="login"
                        animate={{ opacity: 1, width: "auto" }}
                        className="overflow-hidden"
                        exit={{ opacity: 0, width: 0 }}
                        initial={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                      >
                        <ButtonDS
                          className={cn("ml-1", REGULAR)}
                          disabled={code.length !== 6 || !agreedToTerms}
                          isLoading={otpStatus.status === "submitting-code"}
                          size="md"
                          type="submit"
                        >
                          Login
                        </ButtonDS>
                      </motion.span>
                    ) : null}
                  </AnimatePresence>
                  <ButtonDS
                    className={REGULAR}
                    isLoading={otpStatus.status === "sending-code"}
                    onClick={requestCode}
                    size="md"
                  >
                    Resend
                  </ButtonDS>
                </>
              }
            >
              <input
                aria-label="One-time passcode"
                autoComplete="one-time-code"
                autoFocus
                className={cn(INPUT_CLASS, "tracking-[0.02em]")}
                inputMode="numeric"
                maxLength={6}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setError(null);
                }}
                placeholder="Enter the one-time 6 digit passcode"
                value={code}
              />
            </Field>

            <p className="text-body-md-400 text-surface-on-surface-variant">
              We sent a code to <span className="text-surface-on-surface">{email.trim()}</span>.{" "}
              <button
                className="cursor-pointer underline underline-offset-4 hover:text-surface-on-surface"
                onClick={changeEmail}
                type="button"
              >
                Use a different email
              </button>
            </p>

            <TermsCheckbox agreedToTerms={agreedToTerms} onAgreedToTermsChange={setAgreedToTerms} />

            <ErrorLine error={error} />
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}

function ErrorLine(props: { error: string | null }) {
  const { error } = props;

  if (!error) return null;
  return <p className="text-body-md-400 text-sys-negative-negative">{error}</p>;
}

const INPUT_CLASS =
  "text-body-lg-400 min-w-0 flex-1 bg-transparent px-3 text-surface-on-surface outline-none placeholder:text-surface-on-surface-variant";

/** The bordered field with its buttons inside, as the mock draws it. */
function Field(props: { children: ReactNode; action: ReactNode }) {
  const { children, action } = props;

  return (
    <div className="flex h-11.5 items-center gap-1 rounded-button border border-sys-outline-outline bg-white pr-1 focus-within:border-sys-outline-outline-emphasis">
      {children}
      {action}
    </div>
  );
}
